/**
 * Pass 5, 6 and 7 for the flying half of the roster: the blows this tick landed,
 * applied to the states `fly` produced, and the verdict that follows.
 *
 * Lifted out of `encounter-tick.ts` whole — every comment below was written
 * there and is unchanged. It lives beside it rather than inside it because that
 * file's job is the **pass order**, which is the determinism contract and is
 * written out once in its header; the arithmetic of one aeroplane's tick is a
 * separate concern that had grown to a third of the file.
 */

import type { SimState } from "../flight/state.ts";
import { advanceCrashSites, recordCrash, type CrashSite } from "./crash-sites.ts";
import { advanceFire, applyHits } from "./combat-damage.ts";
import type { BuildingStep } from "./building-strike.ts";
import { rackAfter, type BombRun } from "./bomb-pass.ts";
import type { CombatAircraft } from "./encounter.ts";
import { combatEvent, type CombatEvent } from "./events.ts";
import type { Volley } from "./gun-pass.ts";
import type { RoundHit } from "./hit-allocation.ts";
import { aircraftStatus } from "./outcome.ts";
import type { RamResult } from "./ram-pass.ts";
import type { RosterInput } from "./encounter-tick.ts";

export interface Settlement {
  readonly aircraft: CombatAircraft[];
  readonly events: CombatEvent[];
  /** The list carried in, aged one tick, with this tick's arrivals appended (`crash-sites.ts`). */
  readonly crashSites: readonly CrashSite[];
}

export function settle(
  flown: readonly BuildingStep[],
  volley: Volley,
  raid: BombRun,
  input: RosterInput,
  groundFire: readonly RoundHit[],
  ram: RamResult,
): Settlement {
  const events: CombatEvent[] = [];
  const aircraft: CombatAircraft[] = [];
  let crashSites = advanceCrashSites(input.crashSites ?? [], input.dt);
  flown.forEach((stepped, index) => {
    const entry = stepped.entry;
    // A ram first, then gunfire, then archie. `lastAttackerId` takes the head of
    // this list, and the order is the order of how decisive the blow was: a
    // collision ends the airframe outright, so the pilot who was flown into is
    // who the kill belongs to even if a round arrived on the same tick. Archie
    // stays last because a battery is not somebody a career can be shot down by
    // *and credited to*.
    const rammed = ram.hits.filter((hit) => hit.targetId === entry.id);
    const gunfire = volley.hits.filter((hit) => hit.targetId === entry.id);
    const archie = groundFire.filter((hit) => hit.targetId === entry.id);
    const inbound = [...rammed, ...gunfire, ...archie];
    const damaged = applyHits({
      sim: stepped.sim,
      fire: entry.fire,
      hits: inbound,
      rng: input.rng,
      // AIRCRAFT MAY BE DAMAGED spares the *pilot's own* aeroplane, never the
      // ones he is shooting at. Every flight in a mission is stamped with the
      // player's realism (`mission-spec.ts`'s `flightEntry`), so reading the
      // flag off each aeroplane's own config made a NOVICE sortie unwinnable:
      // a full belt from the player and both wingmen left the enemy flight
      // untouched, and a 300-second ace-versus-novice duel never resolved.
      // Flak already scopes its own mercy this way (`flak.ts`'s
      // `target.isPlayer` before `combatLevelScale`); this is the same rule on
      // the gunfire path. A ram stays globally gated — that one is the
      // original's own order of tests at `0x1B084` (`ram-pass.ts`).
      damageable: entry.ai !== null || stepped.sim.config.realism.aircraftMayBeDamaged,
      aircraftId: entry.id,
    });
    const burned = advanceFire(damaged.sim, damaged.fire, input.dt);
    // The clock runs on the tick the aircraft is *already* out of control, so a
    // wreck that has only just been hit gets a full timeout's worth of falling.
    const fallingSeconds = entry.status === "outOfControl" ? entry.fallingSeconds + input.dt : 0;
    const status = aircraftStatus({
      sim: burned.sim,
      fire: burned.fire,
      previous: entry.status,
      fallingSeconds,
    });
    events.push(...damaged.events, ...burned.events);
    const lastAttackerId = inbound[0]?.shooterId ?? entry.lastAttackerId;
    if (status !== entry.status && status !== "flying") {
      events.push(combatEvent(status === "destroyed" ? "destroyed" : "outOfControl", entry.id, lastAttackerId));
    }
    // AD-014c: an aeroplane that *arrived* leaves something behind. Deliberately
    // not every `destroyed` — `aircraftStatus` also writes off a wreck the fall
    // timeout gave up on, and that one is still in the air, so a scorch under it
    // would be a mark on a field nothing touched. The impact point's height is
    // sampled from the encounter's own `GroundQuery`, which is the one surface
    // `applyGround` put the wheels on and the shadow drapes to; nothing here may
    // invent a second answer for where the ground is.
    if (status === "destroyed" && entry.status !== "destroyed" && arrivedOnGround(burned.sim)) {
      crashSites = recordCrash(crashSites, {
        id: entry.id,
        aircraft: burned.sim.config.spec.key,
        x: burned.sim.position.x,
        z: burned.sim.position.z,
        groundY: input.ground.groundHeightAt(burned.sim.position.x, burned.sim.position.z),
        orientation: burned.sim.orientation,
        // A wreck that came down hard, or one that was already alight, burns.
        // One that mushed in under control of nothing in particular does not.
        burning: burned.sim.crashed || burned.fire.burning,
      });
    }
    aircraft.push({
      ...entry,
      sim: burned.sim,
      fire: burned.fire,
      status,
      fallingSeconds,
      roundsFired: entry.roundsFired + (volley.roundsFired[index] ?? 0),
      // Fire taken, which a collision is not — a ram is one event, not the two
      // wing cells it is spelled with.
      hitsTaken: entry.hitsTaken + gunfire.length + archie.length,
      gunHitsTaken: entry.gunHitsTaken + gunfire.length,
      hitsScored: entry.hitsScored + volley.hits.filter((hit) => hit.shooterId === entry.id).length,
      underFire: inbound.length > 0,
      lastAttackerId,
      ...rackAfter(entry, raid.released.has(entry.id), input.tick),
    });
  });
  return { aircraft, events, crashSites };
}

/** True for a wreck that reached the ground, false for one the fall timeout wrote off in mid-air. */
function arrivedOnGround(sim: SimState): boolean {
  return sim.crashed || sim.readout.grounded;
}
